<template>
  <v-dialog :disabled="!responsibleId" :fullscreen="$vuetify.breakpoint.smAndDown" :persistent="loading || isDirty" :value="value"
            content-class="acq-dialog" max-width="760" scrollable @click:outside="onOutside" @input="onDialogInput" @keydown.esc="onOutside">
    <v-card class="acq-dialog mdlg-scope">
      <div class="mdlg-header">
        <span class="acq-avatar acq-avatar--sm tone-warn"><v-icon small style="color: inherit">mdi-account-edit-outline</v-icon></span>
        <div class="mdlg-header__text">
          <div class="acq-section-title">Ansprechpartner:in bearbeiten</div>
          <div class="acq-tiny text-truncate">{{ subtitle }}</div>
        </div>
        <v-btn :disabled="loading" aria-label="Schließen" icon @click="requestClose"><v-icon>mdi-close</v-icon></v-btn>
      </div>
      <v-card-text class="mdlg-body">
        <v-form ref="form" v-model="valid" lazy-validation @submit.prevent="updateResponsible">
          <responsible-form v-if="globalLoading || responsible" v-model="form" :disabled="loading" :job-types="jobTypes"
                            :job-types-loading="jobTypesLoading" :loading="globalLoading" :permissions="permissions"
                            @job-type-created="addJobType"/>
          <!-- QM-Kontakt: marked Ansprechpartner are contacted by the QM instead of the Standort itself -->
          <v-checkbox v-if="responsible && canReadAndUpdate('qmContact')" v-model="qmContact" :disabled="loading" class="mt-2" color="info" dense hide-details
                      label="QM-Kontakt"/>
          <div v-if="responsible && canReadAndUpdate('qmContact')" class="acq-tiny ml-8">Ohne Markierung kontaktiert das QM nur den Standort selbst
            (Mail / Telefon des Standorts); markierte Ansprechpartner:innen stattdessen.</div>
        </v-form>
        <debug-card :value="updateObject"/>
      </v-card-text>
      <div class="mdlg-footer">
        <span v-if="isDirty" class="mdlg-footer__state acq-tiny"><span class="mdlg-dot"/>{{ changeCountText }}</span>
        <span v-else-if="!globalLoading" class="acq-tiny">Keine Änderungen</span>
        <v-spacer/>
        <button :disabled="loading" class="cta cta--ghost" type="button" @click="requestClose">Abbrechen</button>
        <button :disabled="globalLoading || !valid || !isDirty || loading" class="cta cta--primary" type="button" @click="updateResponsible">
          <v-progress-circular v-if="loading" indeterminate size="14" width="2"/>
          <v-icon v-else class="cta__icon" small>mdi-content-save-outline</v-icon>Speichern
        </button>
      </div>
    </v-card>
    <discard-changes-dialog v-model="showDiscard" @confirm="reset"/>
  </v-dialog>
</template>

<script lang="ts">
import Vue from 'vue';
import {Form} from '@/interfaces/Form.interface';
import {Types} from 'mongoose';
import {ResponsibleHandlerAPI} from '@/classes/api/responsible-handler.api.class';
import {Responsible} from '@/interfaces/responsible.interface';
import {CrudEntityTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';
import {CrudActionTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudActionTypes';
import {CrudManagerDirective} from '@/classes/clientOnly/CrudManagerDirective';
import {JobType} from '@/interfaces/jobType.interface';
import {compareForUpdate, isNonEmptyDto, UpdateObject, UpdateType} from '@/helpers/compareForUpdate';
import {ContactInformation} from '@/interfaces/contactInformation.interface';
import {UpdateResponsibleRequestDTO} from '@/classes/dto/responsible-handler/request/UpdateResponsible.request.dto';
import {retrieveObjectId} from '@/helpers/isObjectId.helper';
import {EventBus} from '@/busses/EventBus';
import {contactFormToUpdateDto} from '@/helpers/contactInformationForm.helper';
import DebugCard from '@/components/common/debugCard.vue';
import ResponsibleForm from '@/components/responsibles/responsibleForm.vue';
import DiscardChangesDialog from '@/components/common/modern/discardChangesDialog.vue';
import {
  emptyResponsibleForm,
  JobTypeItem,
  loadJobTypeItems,
  ResponsibleFormPermissions,
  ResponsibleFormValue,
  responsibleFormFromEntity,
} from '@/components/responsibles/responsibleForm.helper';

export default Vue.extend({
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    responsibleId: {
      type: Types.ObjectId,
      required: true,
    },
  },
  components: {DebugCard, DiscardChangesDialog, ResponsibleForm},
  data: () => ({
    valid: true,
    responsible: null as null | Responsible,
    form: emptyResponsibleForm() as ResponsibleFormValue,
    jobTypes: [] as JobTypeItem[],
    jobTypesLoading: false,
    loading: false,
    globalLoading: false,
    showDiscard: false,
    qmContact: false,
  }),
  computed: {
    subtitle(): string {
      if (!this.responsible) {
        return 'Wird geladen …';
      }
      return [this.responsible.firstName, this.responsible.lastName].filter(Boolean).join(' ') || 'Ohne Namen';
    },
    /** Same checks as the former v-can:readAndUpdate-responsibles / v-can:read-jobTypes directives */
    permissions(): ResponsibleFormPermissions {
      const can = (key: string) => this.canReadAndUpdate(key);
      return {
        gender: can('gender'),
        firstName: can('firstName'),
        lastName: can('lastName'),
        birthday: can('birthday'),
        jobType: can('jobType') && CrudManagerDirective.checkAbility({action: CrudActionTypes.READ, entity: CrudEntityTypes.JOB_TYPE}),
        contactInformation: can('contactInformation'),
        createJobType: this.$$crudAccessManager.getAccess(CrudEntityTypes.JOB_TYPE, CrudActionTypes.CREATE),
      };
    },
    updateObject(): UpdateObject<Responsible> {
      if (!this.responsible) {
        return {};
      }
      return {
        gender: compareForUpdate(this.form.gender, this.responsible.gender),
        firstName: compareForUpdate(this.form.firstName, this.responsible.firstName),
        lastName: compareForUpdate(this.form.lastName, this.responsible.lastName),
        jobType: compareForUpdate(this.form.jobType, retrieveObjectId(this.responsible.jobType)),
        birthday: compareForUpdate(this.form.birthday, this.responsible.birthday),
        contactInformation: contactFormToUpdateDto(this.form.contactInformation, this.responsible.contactInformation) as UpdateType<ContactInformation>,
        qmContact: this.canReadAndUpdate('qmContact') && this.qmContact !== !!this.responsible.qmContact ? this.qmContact : undefined,
      };
    },
    isDirty(): boolean {
      return !!this.responsible && isNonEmptyDto(this.updateObject);
    },
    changeCountText(): string {
      const count = Object.values(this.updateObject).filter((v) => v !== undefined).length;
      return count === 1 ? '1 Änderung' : `${count} Änderungen`;
    },
  },
  methods: {
    canReadAndUpdate(key: string): boolean {
      try {
        const entity = this.$$getCrudEntity(CrudEntityTypes.RESPONSIBLE, this.responsibleId);
        return entity.canRead(key) && entity.canUpdate(key);
      } catch (e) {
        return false;
      }
    },
    async loadJobTypes() {
      this.jobTypesLoading = true;
      try {
        this.jobTypes = await loadJobTypeItems();
      } finally {
        this.jobTypesLoading = false;
      }
    },
    addJobType(jobType: JobType) {
      this.jobTypes = [...this.jobTypes, {text: jobType.neutral, value: jobType._id}];
    },
    async getResponsible() {
      try {
        this.globalLoading = true;
        const responsible = await ResponsibleHandlerAPI.findById(this.responsibleId);
        // @ToDo hasLogin not implemented yet
        this.form = responsibleFormFromEntity(responsible);
        this.qmContact = !!responsible.qmContact;
        this.responsible = responsible;
      } catch (e) {
        this.reset();
      } finally {
        this.globalLoading = false;
      }
    },
    async updateResponsible() {
      const form = this.$refs.form as Form;
      if (form && form.validate()) {
        try {
          this.loading = true;
          await ResponsibleHandlerAPI.update(this.responsibleId, this.updateObject as unknown as UpdateResponsibleRequestDTO);
          this.$$showSnackbar(`Ansprechpartner:in wurde erfolgreich aktualisiert`, 'success');
          this.$emit('update:institution');
          this.$emit('update:responsible');
          EventBus.$emit('update:responsible');
          this.reset();
        } catch (e) {
          this.$$showSnackbar(`Bei der Aktualisierung der Ansprechperson ist ein Fehler aufgetreten`, 'error', e);
        } finally {
          this.loading = false;
        }
      }
    },
    onDialogInput(v: boolean) {
      if (!v) {
        this.reset();
      }
    },
    onOutside() {
      if (this.isDirty && !this.loading) {
        this.showDiscard = true;
      }
    },
    requestClose() {
      if (this.loading) {
        return;
      }
      if (this.isDirty) {
        this.showDiscard = true;
        return;
      }
      this.reset();
    },
    reset() {
      this.showDiscard = false;
      this.responsible = null;
      this.form = emptyResponsibleForm();
      this.qmContact = false;
      const form = this.$refs.form as Form | undefined;
      if (form) {
        form.resetValidation();
      }
      this.$emit('input', false);
    },
  },
  mounted() {
    // Prefetch like before, also while the dialog is still closed
    if (!this.value) {
      this.getResponsible();
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(v: boolean) {
        if (v) {
          this.getResponsible();
          if (this.jobTypes.length === 0) {
            this.loadJobTypes();
          }
        }
      },
    },
  },
});
</script>

<style src="@/components/acquisitionList/ui/acquisitionViews.css"></style>
<style src="@/components/common/modern/modern.css"></style>
<style src="@/components/common/modern/modernDialog.css"></style>
